import React from 'react';
import Icon from '../../../components/AppIcon';

const UpcomingUpgradesCard = ({ suppliers }) => {
  const calculateTier = (totalOrders) => {
    const revenue = (totalOrders || 0) * 15;
    if (revenue >= 150000) return 'platinum';
    if (revenue >= 75000) return 'gold';
    if (revenue >= 25000) return 'silver';
    return 'bronze';
  };

  const getNextTier = (currentTier) => {
    if (currentTier === 'bronze') return 'silver';
    if (currentTier === 'silver') return 'gold';
    if (currentTier === 'gold') return 'platinum';
    return null;
  };

  const getNextThreshold = (currentTier) => {
    if (currentTier === 'bronze') return 25000;
    if (currentTier === 'silver') return 75000;
    if (currentTier === 'gold') return 150000;
    return null;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return 'var(--color-primary)';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="TrendingUp" size={24} color="var(--color-success)" />
        <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">Upcoming Upgrades</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Suppliers within 25% of next tier threshold
      </p>

      <div className="space-y-4">
        {suppliers?.slice(0, 5)?.map((supplier) => {
          const currentTier = calculateTier(supplier?.total_orders);
          const nextTier = getNextTier(currentTier);
          const nextThreshold = getNextThreshold(currentTier);
          const revenue = (supplier?.total_orders || 0) * 15;
          const progress = (revenue / nextThreshold) * 100;

          return (
            <div key={supplier?.id} className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Store" size={18} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">{supplier?.business_name}</p>
                    <p className="text-xs text-muted-foreground">{supplier?.user?.email}</p>
                  </div>
                </div>
                <span 
                  className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
                  style={{ 
                    backgroundColor: `${getTierColor(currentTier)}20`,
                    color: getTierColor(currentTier)
                  }}
                >
                  {currentTier}
                </span>
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">
                    Progress to {nextTier}
                  </p>
                  <p className="text-xs font-semibold text-success">{progress?.toFixed(1)}%</p>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-success to-success/70 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ${(nextThreshold - revenue)?.toLocaleString()} to {nextTier} tier
              </p>
            </div>
          );
        })}
        {suppliers?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No suppliers near upgrade threshold</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingUpgradesCard;